<template>
  <div class="mx-auto">
    <!-- Warning message -->
    <div class="flex items-center gap-x-4 bg-white border border-orange-300 text-sm text-gray-700 p-4 rounded-lg mb-8">
      <div>
        <component :is="Info" class="w-5 h-5 text-primary"/>
      </div>
      <p>
        To ensure the success of this operation, you must carefully check the payment document details and correctly
        insert them on this screen. You must indicate a desired payment date that is within the period in which the
        payment can be made, bearing in mind that Bison Account can only guarantee payment on business days, starting on
        the business day following this order. Bison Account does not assume responsibility if the data is entered
        incorrectly or if the requested date is outside the payment period, in which case the payment service fee will
        continue to be due.
      </p>
    </div>

    <!-- Steps -->
    <div class="flex md:flex-row md:justify-between md:items-center md:border-b md:border-gray-200 mb-8 flex-col items-start gap-5">
      <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative" >
        <div :class="['w-8 h-8 flex items-center justify-center rounded-full font-bold z-10', activeStep === 1 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">
          1
        </div>
        <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 1 }]">Fill Data</span>
      </div>
      <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
        <div :class="['w-8 h-8 flex items-center justify-center rounded-full z-10', activeStep === 2 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">2</div>
        <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 2 }]">Check the Operation</span>
      </div>
      <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
        <div :class="['w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-700 rounded-full z-10', activeStep === 3 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">3</div>
        <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 3 }]">Confirmation</span>
      </div>
    </div>

    <!-- Form -->
    <form @submit.prevent class="space-y-8">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- TO WHOM -->
        <div>
          <h3 class="font-semibold text-gray-800 mb-4">TO WHOM?</h3>
          <div class="space-y-4">
            <input type="text" v-model="form.entity" placeholder="Entity *" required
                   class="w-full border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-orange-500 focus:outline-none"/>
            <input type="text" v-model="form.reference" placeholder="Reference *" required
                   class="w-full border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-orange-500 focus:outline-none"/>
          </div>
        </div>

        <!-- HOW MUCH -->
        <div>
          <h3 class="font-semibold text-gray-800 mb-4">HOW MUCH?</h3>
          <div class="space-y-4">
            <div class="flex items-center gap-x-2 relative">
              <input type="number" v-model="form.value" placeholder="Value *" required  :max="selectedAccount?.available_balance || 0"
                     min="0"
                     class="w-full border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-orange-500 focus:outline-none"/>
              <button type="button" class="text-gray-600 bg-gray-200 rounded px-3 py-3" disabled>
                {{ selectedAccount?.currency || 'EUR' }}
              </button>
            </div>

            <input type="date" v-model="form.date" placeholder="Payment date" required
                   class="w-full border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-orange-500 focus:outline-none"/>

            <input type="file" @change="handleFileUpload"
                   class="w-full border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-orange-500 focus:outline-none"/>

            <div v-if="selectedAccount" class="mt-2 text-sm text-gray-700">
              Paying from: <strong>{{ selectedAccount.account_name }} - {{ selectedAccount.account_number }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Buttons for payment type -->
<!--      <div class="flex justify-center gap-4">-->
<!--        <button type="button" @click="submitPayment('services')"-->
<!--                class="px-4 py-3 bg-blue-600 text-white rounded shadow hover:bg-blue-700">-->
<!--          Pay Services-->
<!--        </button>-->
<!--        <button type="button" @click="submitPayment('state')"-->
<!--                class="px-4 py-3 bg-green-600 text-white rounded shadow hover:bg-green-700">-->
<!--          Pay State-->
<!--        </button>-->
<!--      </div>-->


      <div class="flex justify-center">
        <button @click="submitPayment('services')"
                type="button"
            class="w-1/4 flex justify-center px-2 py-3 bg-primary text-white font-semibold rounded-lg shadow"
        >
          PAY
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { reactive, ref, defineProps } from 'vue';
import api from '@/services/api';
import { Info } from 'lucide-vue-next';

const props = defineProps({
  selectedAccount: {
    type: Object,
    default: null
  }
})

const activeStep = ref(2);
const form = reactive({
  entity: '',
  reference: '',
  value: '',
  date: '',
  file: null,
})

const handleFileUpload = (event) => {
  form.file = event.target.files[0]
}

// Submit payment to API
const submitPayment = async (type) => {
  if (!props.selectedAccount) {
    alert('Please select an account first!');
    return;
  }

  try {
    const formData = new FormData();
    formData.append('account_id', props.selectedAccount.id);
    formData.append('type', type); // services | state
    formData.append('entity', form.entity);
    formData.append('reference', form.reference);
    formData.append('value', form.value);
    formData.append('currency', props.selectedAccount.currency || 'EUR');
    formData.append('payment_date', form.date);
    formData.append('description', '-');

    if (form.file) {
      formData.append('file', form.file);
    }

    const token = localStorage.getItem('token'); // فرض می‌کنیم توکن auth در localStorage هست

    const res = await api.post('/payments', formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });

    alert('Payment submitted successfully!');
    console.log(res.data);

    // Clear form after success
    form.entity = '';
    form.reference = '';
    form.value = '';
    form.date = '';
    form.file = null;

  } catch (err) {
    console.error(err);
    alert(err.response?.data?.message || 'Error submitting payment.');
  }
}
</script>
